import { Control, FieldValues, Path } from 'react-hook-form'

import { LittleText } from '@/components/common/UI'
import { CheckboxControl, InputControl } from '@/components/form-controls'

interface Article {
    subtitle?: string
    text?: string[]
    list?: string[]
}

interface CardFormProps<T extends FieldValues> {
    title: string
    description: string
    available?: string
    url?: string
    linkText?: string
    article?: Article[]
    nameControl: Path<T>
    nameControl2?: Path<T>
    acceptTerms: string[]
    required?: string
    inputCategory?: number
    control: Control<T>
    isDisabled?: boolean
}

export const CardForm = <T extends FieldValues>({
    title,
    description,
    available,
    url,
    linkText,
    article,
    nameControl,
    nameControl2,
    acceptTerms,
    required,
    inputCategory = 1,
    control,
    isDisabled = false
}: CardFormProps<T>): JSX.Element => {
    const switchInputs = (type: number): JSX.Element | undefined => {
        switch (type) {
            case 1:
                return (
                    <div className="py-2">
                        <CheckboxControl
                            name={nameControl}
                            control={control}
                            label={acceptTerms[0]}
                            rules={{
                                required: required
                            }}
                            classNameLabel="text-sm"
                            isDisabled={isDisabled}
                        />
                    </div>
                )
            case 2:
                return (
                    <div className="grid grid-rows-2 gap-4 w-full py-2">
                        <InputControl
                            name={nameControl}
                            control={control}
                            label={acceptTerms[0]}
                            rules={{
                                required: required
                            }}
                            className="w-full"
                            isDisabled={isDisabled}
                        />

                        {nameControl2 && (
                            <InputControl
                                name={nameControl2}
                                control={control}
                                label={acceptTerms[1]}
                                className="w-full"
                            />
                        )}
                    </div>
                )
            case 3:
                return (
                    <div className="flex flex-col gap-2 py-2">
                        <CheckboxControl
                            name={nameControl}
                            control={control}
                            label={acceptTerms[0]}
                            rules={{
                                required: required
                            }}
                            classNameLabel="text-sm"
                            isDisabled={isDisabled}
                        />
                        {nameControl2 && (
                            <CheckboxControl
                                name={nameControl2}
                                control={control}
                                label={acceptTerms[1]}
                                rules={{
                                    required: required
                                }}
                                classNameLabel="text-sm"
                                isDisabled={isDisabled}
                            />
                        )}
                    </div>
                )
        }
    }

    return (
        <article className="space-y-1 border-b border-disabled text-justify pt-4">
            <h2 className="text-lg font-semibold">{title}</h2>
            <h4 className="text-sm">{description}</h4>
            {available && url && (
                <div className="flex flex-col gap-2 md:flex-row items-center">
                    <h4 className="text-sm">{available}</h4>

                    <a
                        href={
                            url.startsWith('http://') ||
                            url.startsWith('https://')
                                ? url
                                : `https://${url}`
                        }
                        className="text-xs md:text-sm  underline text-primary hover:text-primary-400 truncate"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {linkText ?? url}
                    </a>
                </div>
            )}

            {article && (
                <div className="p-4 space-y-2 rounded-lg border overflow-y-auto h-32">
                    {article.map((item, index) => (
                        <div key={index}>
                            {item.subtitle && (
                                <h3 className="font-medium text-sm">
                                    {item.subtitle}
                                </h3>
                            )}
                            {item.text &&
                                item.text.map((text, index) => (
                                    <LittleText
                                        key={index}
                                        text={text}
                                    />
                                ))}
                            {item.list && (
                                <ul>
                                    {item.list.map((list, index) => (
                                        <li key={index}>
                                            <LittleText
                                                text={`${index + 1}. ${list}`}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {switchInputs(inputCategory)}
        </article>
    )
}
